/* Write paths for the roles-permissions bundle (RolesView / PermissionsView),
   ported VERBATIM from app.ts: saveRole (~4754), patchRole (~4818), the role
   editor open, grantRole (~4981), revokeGrant (~4990), mutateGrant (~4996).

   The vanilla client hung these on the module, mutating the single `state`
   object and calling render(doc); the React shell's peer of that is a Store
   action (mutate this.state, then commit()). This bundle is not allowed to
   edit shell/store.ts, so its writes live here as free functions that take the
   Store, mutate store.state, and call store.commit() — the exact same shape an
   equivalent store action would have. Every REST call is byte-identical to the
   vanilla source (same endpoint, method, and body) — the API contract is
   frozen. `failureReason`/`unauthoredReason` are copied verbatim from
   shell/store.ts (they are module-private there) so a server refusal is
   surfaced with the same reason the vanilla client showed. */
import type { Role, RoleEdit } from "../../../shared/roles";
import { cleanRoleInput, validateRoleInput } from "../../../shared/roles";
import { loadJson, type Store } from "../shell/store";
import type { UserWithGrants } from "../shell/state";

/* --- failure reasons, verbatim from shell/store.ts (private there) --- */

async function failureReason(
  res: { status?: number; json(): Promise<unknown> },
  fallback: string,
): Promise<string> {
  try {
    const { error } = (await res.json()) as { error?: string };
    if (error) return error;
  } catch {
    /* no readable body — fall through to the status */
  }
  return unauthoredReason(res.status, fallback);
}

function unauthoredReason(status: number | undefined, fallback: string): string {
  const action = fallback.split(" — ")[0];
  if (status === 401 || status === 403)
    return `${action} — the request was refused before it reached the app; reload the page to sign in again. (HTTP ${status})`;
  return status === undefined ? fallback : `${fallback} (HTTP ${status})`;
}

/* --- Roles catalog writes --- */

/* The add itself (AA2): posts, and a refusal is surfaced VERBATIM so the actor
   reads the server's own reason. The client validates nothing the server does
   not re-check; validateRoleInput just says the same rules sooner (D8). */
export async function saveRole(store: Store): Promise<void> {
  /* the request may already be in flight — the vanilla client disabled the
     button imperatively to stop a duplicate POST (review finding 4); the React
     button reads state.saving, and this guard closes the same gap */
  if (store.state.saving) return;
  const input = cleanRoleInput({
    name: store.state.roleQuery,
    description: store.state.roleDescription,
    cardinality: store.state.roleCardinality,
  });
  const reason = validateRoleInput(input, store.state.roles);
  if (reason) {
    store.state.listError = reason;
    return store.commit();
  }
  const { name, description, cardinality } = input;
  store.state.listError = null;
  store.state.saving = true;
  store.commit();
  try {
    const res = await fetch("/api/roles", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ name, description, cardinality }),
    });
    if (!res.ok) {
      store.state.listError = await failureReason(res, "Save failed — is the server healthy?");
    } else {
      /* re-read rather than push the response into the list: the server is the
         authority on what the catalog now holds */
      store.state.roles = await loadJson<Role[]>("/api/roles");
      store.state.roleQuery = "";
      store.state.roleDescription = "";
      store.state.roleCardinality = "many";
    }
  } catch {
    store.state.listError = "Save failed — is the server healthy?";
  }
  store.state.saving = false;
  store.commit();
}

/* Open the editor prefilled with what the role already says — an editor that
   starts empty invites clearing a description by accident. */
export function openRoleEditor(store: Store, id: string): void {
  const role = store.state.roles.find((r) => r.id === id) as Role;
  store.state.editingRoleId = id;
  store.state.editRoleName = role.name;
  store.state.editRoleDescription = role.description ?? "";
  store.state.editRoleCardinality = role.cardinality;
  store.state.listError = null;
  store.state.roleConflicts = [];
  store.commit();
}

/* Apply a correction (CR12/CR13). The role is addressed BY ID in the path; the
   body never carries one, so a rename cannot re-point what is recorded against
   it. The server re-checks every rule and its refusals are shown verbatim. */
export async function patchRole(store: Store, id: string, edit: RoleEdit): Promise<void> {
  store.state.listError = null;
  store.state.roleConflicts = [];
  try {
    const res = await fetch(`/api/roles/${encodeURIComponent(id)}`, {
      method: "PATCH",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(edit),
    });
    if (!res.ok) {
      store.state.listError = await failureReason(res, "Save failed — is the server healthy?");
    } else {
      const saved = (await res.json()) as Role & { conflicts?: string[] };
      store.state.roleConflicts = saved.conflicts ?? [];
      store.state.roles = await loadJson<Role[]>("/api/roles");
      store.state.editingRoleId = null;
    }
  } catch {
    store.state.listError = "Save failed — is the server healthy?";
  }
  store.commit();
}

export function cancelRoleEdit(store: Store): void {
  store.state.editingRoleId = null;
  store.commit();
}

/* retire/bring-back is a correction like any other (CR8/CR16) */
export async function toggleRetire(store: Store, role: Role): Promise<void> {
  await patchRole(store, role.id, { retired: !role.retired });
}

/* --- Permissions grant writes --- */

export async function grantRole(store: Store, userId: string, roleId: string): Promise<void> {
  await mutateGrant(store, `/api/users/${userId}/roles`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ roleId }),
  });
}

export async function revokeGrant(
  store: Store,
  userId: string,
  roleId: string,
  teamId?: string,
): Promise<void> {
  const team = teamId ? `?team=${encodeURIComponent(teamId)}` : "";
  await mutateGrant(store, `/api/users/${userId}/roles/${roleId}${team}`, { method: "DELETE" });
}

/* apply a grant change, then re-read the list — the server is the authority */
async function mutateGrant(store: Store, path: string, init: RequestInit): Promise<void> {
  store.state.listError = null;
  try {
    const res = await fetch(path, init);
    if (!res.ok) {
      store.state.listError = await failureReason(res, "Change failed — is the server healthy?");
      return store.commit();
    }
    store.state.users = await loadJson<UserWithGrants[]>("/api/users");
  } catch {
    store.state.listError = "Change failed — is the server healthy?";
  }
  store.commit();
}

